import React from "react";
import { useTeamsIntegration } from "src/hooks/useTeamsIntegration";
import { Button } from "src/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import { Video, CheckCircle, AlertCircle, RefreshCw, LogOut } from "lucide-react";

const MicrosoftTeamsIntegration: React.FC = () => {
  const {
    isAuthenticated,
    isInitializing,
    authenticate,
    signOut,
    isTeamsEnabled,
  } = useTeamsIntegration();

  const handleConnect = async () => {
    try {
      await authenticate();
    } catch (error) {
      console.error('Failed to authenticate with Teams:', error);
    }
  };

  const handleDisconnect = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to disconnect from Teams:', error);
    }
  };

  if (!isTeamsEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Video className="h-5 w-5 mr-2 text-gray-400" />
            Microsoft Teams
          </CardTitle>
          <CardDescription>
            Microsoft Teams integration is currently disabled
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">
              Teams integration is not available at this time.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isInitializing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Video className="h-5 w-5 mr-2 text-blue-500" />
            Microsoft Teams
          </CardTitle>
          <CardDescription>
            Checking Teams integration status...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <RefreshCw className="h-8 w-8 mx-auto text-blue-500 animate-spin mb-4" />
            <p className="text-gray-500">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-6">
        {!isAuthenticated ? (
          <div className="text-center py-6">
            <Video className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium mb-2">
              Test: Connect to Microsoft Teams
            </h3>
            <p className="text-gray-500 mb-4">
              Link your Microsoft 365 account to create Teams meetings as the organizer
              with full meeting controls.
            </p>
            <Button onClick={handleConnect} className="bg-blue-600 hover:bg-blue-700">
              Connect to Microsoft Teams
            </Button>
            <div className="mt-4 text-sm text-gray-600">
              <p className="font-medium mb-2">What you'll get:</p>
              <ul className="text-left space-y-1 max-w-md mx-auto">
                <li>• Full organizer privileges for all meetings</li>
                <li>• Students can join anonymously (no Microsoft account needed)</li>
                <li>• Automatic meeting creation for scheduled sessions</li>
                <li>• Meeting controls (mute, record, end meeting)</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start">
              <div className="bg-green-100 rounded-full p-2 mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-green-800">
                  Connected to Microsoft Teams
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  Your Microsoft 365 account is successfully linked. You can now create
                  Teams meetings as the organizer for your tutoring sessions.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="font-medium text-blue-800 mb-2">
                  Integration Benefits
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✅ You are the meeting organizer with full control</li>
                  <li>✅ Students join anonymously without Microsoft accounts</li>
                  <li>✅ Meetings automatically created for scheduled sessions</li>
                  <li>✅ Access to recording, muting, and other organizer features</li>
                  <li>✅ No need to re-authenticate for each meeting</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <h4 className="font-medium text-yellow-800 mb-2">
                  Important Notes
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Your Microsoft 365 account is required for organizer privileges</li>
                  <li>• Meetings are created using your authenticated account</li>
                  <li>• Students can join meetings without any Microsoft account</li>
                  <li>• Your authentication tokens are securely stored and auto-refreshed</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </CardContent>
      {isAuthenticated && (
        <CardFooter className="flex justify-between border-t pt-6">
          <Button variant="outline" onClick={() => window.open('https://teams.microsoft.com', '_blank')}>
            <Video className="h-4 w-4 mr-2" />
            Open Teams
          </Button>
          <Button
            variant="outline"
            className="text-red-500 hover:text-red-700"
            onClick={handleDisconnect}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Disconnect
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default MicrosoftTeamsIntegration;
